/**
 * Lat/lng parsing and swap correction — aligned with buxusplatform-frontend
 * `src/app/shared/utils/geo-utils.ts` (`parseLatLngPair`).
 *
 * Portal map plotting (contacts/campaigns) additionally uses North America bounds via
 * `correctSwappedCoordinates` in ContactPlottingService; use {@link parseLatLngPairNorthAmerica}
 * when NA-only validation is required after correction.
 */

const NUMERIC_ONLY = /^-?\d+(\.\d+)?$/;

/**
 * Decimal string for payloads (analytics, API, CRM) — never rely on JSON numbers for coordinates.
 * String inputs are trimmed; finite numbers use plain decimal notation (no scientific notation).
 */
export function coordinateToDecimalString(
  value: number | string | null | undefined,
): string | undefined {
  if (value === null || value === undefined) return undefined;
  if (typeof value === "string") {
    const t = value.trim().replace(/\s+/g, "");
    return t === "" ? undefined : t;
  }
  if (typeof value === "number") {
    if (!Number.isFinite(value)) return undefined;
    if (Object.is(value, -0)) return "0";
    const s = String(value);
    if (!/e/i.test(s)) return s;
    return value.toFixed(15).replace(/(?:\.0+|(\.\d+?)0+)$/, "$1");
  }
  return undefined;
}

/** Global lat/lng bounds (portal `geo-utils.ts`). */
export const GLOBAL_LAT_MIN = -90;
export const GLOBAL_LAT_MAX = 90;
export const GLOBAL_LNG_MIN = -180;
export const GLOBAL_LNG_MAX = 180;

/** North America bounds (portal `geo-utils.ts` / ContactPlottingService). */
export const NORTH_AMERICA_LAT_MIN = 24.5;
export const NORTH_AMERICA_LAT_MAX = 49.5;
export const NORTH_AMERICA_LNG_MIN = -125;
export const NORTH_AMERICA_LNG_MAX = -66;

/**
 * Parses and validates a single lat/lng pair (string or number).
 * Returns Mapbox order `[lng, lat]` if valid within global bounds (and corrects swap if needed), else null.
 *
 * Swap rule (same as portal): if the value in the lat field is not a valid latitude (|lat| > 90)
 * but swapping yields valid global bounds (|lng| <= 90 and |lat| <= 180), treat fields as reversed.
 */
export function parseLatLngPair(
  lat: number | string | null | undefined,
  lng: number | string | null | undefined,
): [number, number] | null {
  const latStr = String(lat ?? "")
    .trim()
    .replace(/\s+/g, "");
  const lngStr = String(lng ?? "")
    .trim()
    .replace(/\s+/g, "");
  if (latStr === "" || lngStr === "") return null;
  if (!NUMERIC_ONLY.test(latStr) || !NUMERIC_ONLY.test(lngStr)) return null;

  const latNum = parseFloat(latStr);
  const lngNum = parseFloat(lngStr);
  if (!Number.isFinite(latNum) || !Number.isFinite(lngNum)) return null;

  const withinLat = Math.abs(latNum) <= 90;
  const withinLng = Math.abs(lngNum) <= 180;
  if (withinLat && withinLng) return [lngNum, latNum];

  const swappedWithinLat = Math.abs(lngNum) <= 90;
  const swappedWithinLng = Math.abs(latNum) <= 180;
  if (swappedWithinLat && swappedWithinLng) return [latNum, lngNum];

  return null;
}

/**
 * Map center helper: {@link parseLatLngPair} plus optional rejection of (0,0) from empty legacy fields.
 */
export function parseMapboxLngLat(
  lat: number | string | null | undefined,
  lng: number | string | null | undefined,
  options?: { rejectOrigin?: boolean },
): [number, number] | null {
  const pair = parseLatLngPair(lat, lng);
  if (!pair) return null;
  if (options?.rejectOrigin && Math.abs(pair[0]) < 1e-8 && Math.abs(pair[1]) < 1e-8) return null;
  return pair;
}

/** Portal `isValidLatLng` — bounds check on already-corrected lat/lng numbers. */
export function isValidLatLng(
  lat?: number,
  lng?: number,
  bounds: "global" | "northAmerica" = "global",
): boolean {
  if (lat === undefined || lng === undefined) return false;
  if (bounds === "global") {
    return (
      lat >= GLOBAL_LAT_MIN &&
      lat <= GLOBAL_LAT_MAX &&
      lng >= GLOBAL_LNG_MIN &&
      lng <= GLOBAL_LNG_MAX
    );
  }
  return (
    lat >= NORTH_AMERICA_LAT_MIN &&
    lat <= NORTH_AMERICA_LAT_MAX &&
    lng >= NORTH_AMERICA_LNG_MIN &&
    lng <= NORTH_AMERICA_LNG_MAX
  );
}

/**
 * North America swap correction (portal ContactPlottingService.correctSwappedCoordinates).
 * Returns corrected `{ lat, lng }` or null if neither order fits NA bounds.
 */
export function parseLatLngPairNorthAmerica(
  lat?: number,
  lng?: number,
): { lat: number; lng: number } | null {
  if (lat === undefined || lng === undefined) return null;

  const latValid = lat >= NORTH_AMERICA_LAT_MIN && lat <= NORTH_AMERICA_LAT_MAX;
  const lngValid = lng >= NORTH_AMERICA_LNG_MIN && lng <= NORTH_AMERICA_LNG_MAX;
  if (latValid && lngValid) return { lat, lng };

  const latSwapped = lng >= NORTH_AMERICA_LAT_MIN && lng <= NORTH_AMERICA_LAT_MAX;
  const lngSwapped = lat >= NORTH_AMERICA_LNG_MIN && lat <= NORTH_AMERICA_LNG_MAX;
  if (latSwapped && lngSwapped) return { lat: lng, lng: lat };

  return null;
}

/** Read first present key from a payload row (camelCase / PascalCase). */
export function readCoordField(row: Record<string, unknown>, keys: readonly string[]): unknown {
  for (const k of keys) {
    if (k in row) return row[k];
  }
  return undefined;
}

const LAT_KEYS = ["lat", "Lat", "latitude", "Latitude"] as const;
const LNG_KEYS = ["long", "Long", "lng", "longitude", "Longitude"] as const;

/** Operator / asset row → Mapbox `[lng, lat]` using global swap rules. */
export function parseMapboxLngLatFromRecord(
  row: Record<string, unknown> | null | undefined,
  options?: { rejectOrigin?: boolean },
): [number, number] | null {
  if (!row) return null;
  const lat = readCoordField(row, LAT_KEYS);
  const lng = readCoordField(row, LNG_KEYS);
  const latArg =
    typeof lat === "number" || typeof lat === "string" ? lat : lat == null ? null : undefined;
  const lngArg =
    typeof lng === "number" || typeof lng === "string" ? lng : lng == null ? null : undefined;
  return parseMapboxLngLat(latArg, lngArg, options);
}
